<script lang="ts">
    import { computed, onBeforeUnmount, reactive } from 'vue';
    import { bitshift, requestFrame, cancelFrame, uid, calc_snap } from '@utils';
    import vm, { JourneyLines, JourneyLine, JourneyNodes, JourneyNode, LineVertex } from '@journeys/Journeys.viewmodel';
    import { bsAlert } from '@shared/components/BsAlerts';

    // directives
    import { vWhen } from '@journeys/directives';
    import { NodeFrame } from '../NodePartials/NodeAssets';

    // enums
    import { RelVertexFunc } from '@journeys/journeys.d';

    // components
    import JourneyLineGroup from './JourneyLine.vue';

    // assets
    import PresetColors from '@shared/components/ColorPickers/PresetColors';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'CanvasLayer_Lines',
    };
    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    import { validateConnectionType } from '@journeys/utils/validateNodeConnection';
    import { $Resources } from '@cwd/lib/main';
    import { NodeType } from '@journeys/Enums';

    const props = withDefaults(
        defineProps<{
            data: JourneyLines;
            nodeList: JourneyNodes;
        }>(),
        {}
    );

    const { settings, editor, canvas, toast } = vm,
        { p_mod } = settings.canvas,
        /** local state */
        state = reactive({
            busy: false,
        });

    // Track active document event listeners for cleanup
    const activeDocumentListeners: Array<{ type: string; handler: EventListener }> = [];

    const addDocumentListener = (type: string, handler: EventListener | ((ev: any) => void)) => {
        const listener = handler as EventListener;
        document.addEventListener(type, listener);
        activeDocumentListeners.push({ type, handler: listener });
    };

    const removeDocumentListener = (type: string, handler: EventListener | ((ev: any) => void)) => {
        const listener = handler as EventListener;
        document.removeEventListener(type, listener);
        const index = activeDocumentListeners.findIndex((l) => l.type === type && l.handler === listener);
        if (index > -1) {
            activeDocumentListeners.splice(index, 1);
        }
    };

    onBeforeUnmount(() => {
        // Remove editor event listeners
        editor.events.removeListener('newlinehitboxes');
        editor.events.removeListener('changevertexfunc');
        editor.events.removeListener('initline');
        editor.events.removeListener('deleteline');

        // Remove any active document event listeners
        activeDocumentListeners.forEach(({ type, handler }) => {
            document.removeEventListener(type, handler);
        });
        activeDocumentListeners.length = 0;

        // Cancel any pending frame requests
        if (lineFrameReq) {
            cancelFrame(lineFrameReq);
        }
    });

    let lineFrameReq: number;
    const addPoint = (ev: PointerEvent, line: JourneyLine, idx: number) => {
        state.busy = true;
        editor.state.busy = true;

        canvas.el?.setPointerCapture(ev.pointerId);

        let snap = settings.grid.snap,
            snap_spacing = settings.grid.spacing,
            { x, y } = snap ? calc_snap({ x: ev.offsetX, y: ev.offsetY }, snap_spacing) : { x: ev.offsetX, y: ev.offsetY },
            newPoint = reactive({
                x,
                y,
            });

        line.points.splice(idx, 0, newPoint);

        const movePoint = (ev: any) => {
            editor.state.moving = true;

            //** VENDOR BUG: firefox nullifies ev.currentTarget property after requestFrame
            //** TEMP_FIX: cache values prior next frame
            let { x, y } = snap ? calc_snap({ x: ev.offsetX, y: ev.offsetY }, snap_spacing) : { x: ev.offsetX, y: ev.offsetY };

            cancelFrame(lineFrameReq);
            lineFrameReq = requestFrame(() => {
                newPoint.x = x;
                newPoint.y = y;
            });
        };

        addDocumentListener('pointermove', movePoint);
        const pointerUpHandler = () => {
            state.busy = false;
            editor.state.busy = false;
            editor.state.moving = false;

            generateHitboxes(line);
            removeDocumentListener('pointermove', movePoint);
            removeDocumentListener('pointerup', pointerUpHandler);
        };
        addDocumentListener('pointerup', pointerUpHandler);
    };

    const generateHitboxes = (line: JourneyLine) => {
        if (!line) return;
        if (line.points.length > 0) {
            line.runtime.hitboxes = line.points.map((point: any, idx: number) => {
                let hb = { x: 0, y: 0, ex: 0, ey: 0 };
                if (idx === 0) {
                    hb.x = line.x;
                    hb.y = line.y;
                } else {
                    hb.x = line.points[idx - 1].x;
                    hb.y = line.points[idx - 1].y;
                }
                if (idx === line.points.length) {
                    hb.ex = line.ex;
                    hb.ey = line.ey;
                } else {
                    hb.ex = line.points[idx].x;
                    hb.ey = line.points[idx].y;
                }

                return hb;
            });
            line.runtime.hitboxes.push({
                x: line.points[line.points.length - 1].x,
                y: line.points[line.points.length - 1].y,
                ex: line.ex,
                ey: line.ey,
            });
        } else {
            line.runtime.hitboxes = [
                {
                    x: line.x,
                    y: line.y,
                    ex: line.ex,
                    ey: line.ey,
                },
            ];
        }
    };

    editor.events.addListener('newlinehitboxes', generateHitboxes);

    const calcRelVertex = {
        0: null,
        // some trigonometry .. bit heavy though
        // enum: Rect
        1: (point: LineVertex, node: JourneyNode) => {
            // let nx = node.x + (point.mdx || 0),
            //    ny = node.y + (point.mdy || 0),
            let nx = node.x + point.mdx,
                ny = node.y + point.mdy,
                nw = node.w + settings.canvas.relVertexMargin,
                nh = node.h + settings.canvas.relVertexMargin,
                nw_2 = nw / 2,
                nh_2 = nh / 2,
                cx = nx + nw_2 - settings.canvas.relVertexMargin / 2,
                cy = ny + nh_2 - settings.canvas.relVertexMargin / 2,
                theta = Math.atan2(cy - point.y, cx - point.x),
                atan = {
                    w: Math.atan(nh / nw),
                    h: Math.atan(nw / nh),
                },
                tan_sub_h = Math.tan(theta - Math.PI / 2) * nh_2,
                tan_w = Math.tan(theta) * nw_2;

            // -y
            let x = cx + tan_sub_h;
            let y = cy - nh_2;

            // -x
            if (theta < atan.w && theta > -atan.w) {
                x = cx - nw_2;
                y = cy - tan_w;
            }

            // y
            if (theta <= -atan.w && theta > -atan.h * 2 - atan.w) {
                x = cx - tan_sub_h;
                y = cy + nh_2;
            }

            // x
            if (theta <= -atan.h * 2 - atan.w || theta > atan.h * 2 + atan.w) {
                x = cx + nw_2;
                y = cy + tan_w;
            }

            return { x: bitshift.round(x), y: bitshift.round(y) };
        },

        // enum: RotatedRect
        // for now only handles 90deg
        2: (point: LineVertex, node: JourneyNode) => {
            // let nx = node.x + (point.mdx || 0),
            //    ny = node.y + (point.mdy || 0),
            let nx = node.x + point.mdx,
                ny = node.y + point.mdy,
                nw = node.w + settings.canvas.relVertexMargin / 2,
                nh = node.h + settings.canvas.relVertexMargin / 2,
                nw_3 = nw / 3,
                nh_3 = nw / 3,
                // pi: 1/2, 1/4, 3/4
                pi = Math.PI,
                pi_12 = Math.PI / 2,
                pi_14 = Math.PI / 4,
                // center x, y
                cx = nx + nw / 2 - settings.canvas.relVertexMargin / 4,
                cy = ny + nh / 2 - settings.canvas.relVertexMargin / 4,
                // atan2
                theta = Math.atan2(cy - point.y, cx - point.x),
                // tan: 1/4 - pi: 1/4
                tan_sub_pi_14 = Math.tan(theta - pi_14),
                tan_sub_w = tan_sub_pi_14 * nw_3,
                tan_sub_h = tan_sub_pi_14 * nh_3,
                // tan: 1/4 + pi: 1/4
                tan_add_pi_14 = Math.tan(theta + pi_14),
                tan_add_w = tan_add_pi_14 * nw_3,
                tan_add_h = tan_add_pi_14 * nh_3;

            // -y-x
            let x = cx - nw_3 + tan_sub_h,
                y = cy - nw_3 - tan_sub_h;

            // -x+y
            if (theta <= 0 && theta > -pi_12) {
                x = cx - nw_3 - tan_add_w;
                y = cy + nw_3 - tan_add_w;
            }

            // +y+x
            if (theta <= -pi_12 && theta > -pi) {
                x = cx + nh_3 - tan_sub_h;
                y = cy + nh_3 + tan_sub_h;
            }

            // +x-y
            if (theta <= pi && theta > pi_12) {
                x = cx + nw_3 + tan_add_w;
                y = cy - nw_3 + tan_add_w;
            }

            return { x: bitshift.round(x), y: bitshift.round(y) };
        },

        // enum: Circ
        // its a circle (`_´)
        3: (point: LineVertex, node: JourneyNode, zero?: number) => {
            // let nx = node.x + (point.mdx || 0),
            //    ny = node.y + (point.mdy || 0),
            let nx = node.x + point.mdx,
                ny = node.y + point.mdy,
                nw = (node.w + settings.canvas.relVertexMargin) / 2,
                nh = (node.h + settings.canvas.relVertexMargin) / 2,
                cx = nx + nw - settings.canvas.relVertexMargin / 2,
                cy = ny + nh - settings.canvas.relVertexMargin / 2,
                vx = point.x - cx || zero!,
                vy = point.y - cy,
                len = Math.sqrt(vx ** 2 + vy ** 2),
                x = cx + (vx / len) * nw,
                y = cy + (vy / len) * nh;

            return { x: bitshift.round(x), y: bitshift.round(y) };
        },
    };

    const newLine = ({ x, y, direction, node }: { x: number; y: number; direction?: string; node?: any }) => {
        let id = editor.tools.seq_iid(),
            to: string | null = null,
            fr: string | null = null,
            ex: number = x,
            ey: number = y;

        props.data[id] = {
            id,
            // ed: [
            //    id,
            //    x, y, ex, ey, []
            // ],
            to,
            fr,
            // to,
            // fr,
            x,
            y,
            ex,
            ey,
            points: [],

            runtime: {
                show: () => {},
                hitboxes: [],
                toVertex: null,
                frVertex: null,
                toVertexMod: {
                    x: 0,
                    y: 0,
                },
                frVertexMod: {
                    x: 0,
                    y: 0,
                },
            },
        };

        setRuntimeData({ id, direction, node });

        return props.data[id];
    };

    const setRuntimeData = ({ id, direction, node }: { id: number; direction?: string; node: JourneyNode }) => {
        if (!node) return;

        let line = props.data[id],
            relVertexFunc = node.data.type === NodeType.Page ? calcRelVertex[RelVertexFunc.Rect] : calcRelVertex[RelVertexFunc.Circ];

        // if ([NodeFrame.RoundedRect, NodeFrame.RoundedSquare].indexOf(node.f) >= 0)
        //    relVertexFunc = calcRelVertex[RelVertexFunc.Rect]
        // else if ([NodeFrame.Rombus].indexOf(node.f) >= 0)
        //    relVertexFunc = calcRelVertex[RelVertexFunc.RotatedRect]

        node.runtime.relVertexFunc = relVertexFunc;

        let points = line.points;

        if (direction === 'to') {
            line.fr = node.id;

            let frVertexFrameReq = 0;
            line.runtime.frVertex = computed(() => {
                let point = {
                    x: line.ex,
                    y: line.ey,
                    mdx: line.runtime.toVertexMod.x,
                    mdy: line.runtime.toVertexMod.y,
                };

                if (points.length > 0) {
                    let { x, y } = points[0];
                    point.x = x;
                    point.y = y;
                }

                let { x, y } = node.runtime.relVertexFunc!(point, node, 1);

                cancelFrame(frVertexFrameReq);
                frVertexFrameReq = requestFrame(() => {
                    line.x = x;
                    line.y = y;
                });
                return `${x} ${y}`;
            });
        } else if (direction === 'fr') {
            line.to = node.id;

            let toVertexFrameReq = 0;
            line.runtime.toVertex = computed(() => {
                let point: LineVertex = {
                    x: line.x,
                    y: line.y,
                    mdx: line.runtime.frVertexMod.x,
                    mdy: line.runtime.frVertexMod.y,
                };

                if (points.length > 0) {
                    let { x, y } = points[points.length - 1];
                    point.x = x;
                    point.y = y;
                }

                let { x, y } = node.runtime.relVertexFunc!(point, node, -1);

                cancelFrame(toVertexFrameReq);
                toVertexFrameReq = requestFrame(() => {
                    line.ex = x;
                    line.ey = y;
                });

                return `${x} ${y}`;
            });
        }
    };

    const initLine = (line: JourneyLine) => {
        line.runtime = {
            el: undefined,
            hitboxes: [],
            toVertex: null,
            frVertex: null,
            toVertexMod: {
                x: 0,
                y: 0,
            },
            frVertexMod: {
                x: 0,
                y: 0,
            },
            show: () => {},
        };

        line.fr && setRuntimeData({ id: line.id, direction: 'to', node: props.nodeList[line.fr] as JourneyNode });
        line.to && setRuntimeData({ id: line.id, direction: 'fr', node: props.nodeList[line.to] as JourneyNode });

        generateHitboxes(line);
    };

    editor.events.addListener('initline', initLine);

    Object.values(props.data).forEach((line: JourneyLine) => {
        initLine(line);
    });

    const drawLine = (ev: PointerEvent & { target: HTMLElement }, connecting?: { direction: string; node: JourneyNode }) => {
        let triggerMarginPassed = false;
        editor.state.busy = true;

        canvas.el?.setPointerCapture(ev.pointerId);

        let snap = settings.grid.snap,
            snap_spacing = settings.grid.spacing,
            nl: any,
            o = {
                x: ev.offsetX,
                y: ev.offsetY,
            },
            px = connecting?.direction === 'fr' ? 'x' : 'ex',
            py = connecting?.direction === 'fr' ? 'y' : 'ey';

        const handleDraw = (ev: any) => {
            let { x, y } = snap ? calc_snap({ x: ev.offsetX, y: ev.offsetY }, snap_spacing) : { x: ev.offsetX, y: ev.offsetY };

            if (!triggerMarginPassed && Math.abs(o.x - ev.offsetX) + Math.abs(o.y - ev.offsetY) > 10) {
                triggerMarginPassed = true;

                if (connecting) {
                    let { direction, node } = connecting;
                    nl = newLine({
                        x: node.x + node.w / 2,
                        y: node.y + node.h / 2,
                        direction,
                        node,
                    });

                    let nd = direction === 'to' ? node.lnto : node.lnfr;
                    nd[nl.id] = {
                        id: nl.id,
                    };
                } else {
                    nl = newLine({
                        x,
                        y,
                    });
                }
            }

            if (!triggerMarginPassed || !props.data[nl.id]) return;

            cancelFrame(lineFrameReq);
            lineFrameReq = requestFrame(() => {
                props.data[nl.id][px as 'x'] = x;
                props.data[nl.id][py as 'y'] = y;
            });
        };

        addDocumentListener('pointermove', handleDraw);
        const pointerUpHandler = (ev: PointerEvent) => {
            // state.busy = false
            editor.state.busy = false;

            cancelFrame(lineFrameReq);
            removeDocumentListener('pointermove', handleDraw);
            removeDocumentListener('pointerup', pointerUpHandler);

            if (!triggerMarginPassed) {
                if (connecting) editor.tools.sel_nodes = { [connecting.node.id]: connecting.node };
                return;
            }

            if (Math.abs(props.data[nl.id].ex - props.data[nl.id].x) + Math.abs(props.data[nl.id].ey - props.data[nl.id].y) < 30) {
                // just in case, check all
                let node_fr = props.nodeList[nl.fr] as JourneyNode,
                    node_to = props.nodeList[nl.to] as JourneyNode;

                delete node_fr?.lnto[nl.id];
                delete node_to?.lnfr[nl.id];

                delete props.data[nl.id];
                return;
            }

            generateHitboxes(nl);
            connectToNode({
                x: ev.x,
                y: ev.y,
                line: props.data[nl.id],
                direction: connecting?.direction || 'to',
            });

            editor.events.emit('calcselarea', {
                nodes: editor.tools.sel_nodes,
                points: editor.tools.sel_points,
                lines: editor.tools.sel_lines,
            });
        };
        addDocumentListener('pointerup', pointerUpHandler);
    };

    const handleMovePoint = (ev: PointerEvent, line: JourneyLine, target: any, px: string, py: string, direction?: string | null, idx?: number | string) => {
        state.busy = true;
        editor.state.busy = true;

        canvas.el?.setPointerCapture(ev.pointerId);

        let snap = settings.grid.snap,
            snap_spacing = settings.grid.spacing,
            modSelect = true,
            nodes: Array<any> = Object.values(editor.tools.sel_nodes),
            nodes_len = nodes.length,
            points: Array<any> = Object.values(editor.tools.sel_points),
            point_len: number = points.length,
            lines: Array<any> = Object.values(editor.tools.sel_lines),
            line_len: number = points.length,
            moveSelection = nodes_len + point_len + line_len > 0 ? true : false,
            point = {
                id: line.id,
                idx: idx,
                cid: `l${line.id}${typeof idx === 'number' ? 'p' + idx : idx}`,
                x: line.x,
                y: line.y,
            },
            isSelected = editor.tools.sel_points[point.cid];

        if (typeof idx === 'number') {
            point.x = line.points[idx].x;
            point.y = line.points[idx].y;
        } else if (idx === 'to') {
            point.x = line.ex;
            point.y = line.ey;
        }

        let o: any = {
            // x: ev.offsetX,
            // y: ev.offsetY
            x: ev.x,
            y: ev.y,
            px: point.x,
            py: point.y,
            sel_area: {
                x: editor.tools.sel_dims.l,
                y: editor.tools.sel_dims.t,
            },
        };

        if (moveSelection) {
            for (let sn of nodes) {
                sn.runtime ||= {};
                sn.runtime.nx = sn.x;
                sn.runtime.ny = sn.y;
            }

            for (let pt of points) {
                if (typeof pt.idx === 'number') {
                    pt.x = props.data[pt.id].points[pt.idx].x;
                    pt.y = props.data[pt.id].points[pt.idx].y;
                } else if (pt.idx === 'to') {
                    pt.ex = props.data[pt.id].ex;
                    pt.ey = props.data[pt.id].ey;
                } else if (pt.idx === 'fr') {
                    pt.x = props.data[pt.id].x;
                    pt.y = props.data[pt.id].y;
                }
            }
        }

        let detachTriggered = false;
        const detach = () => {
            if (direction === 'fr' && line.fr) {
                let node_fr = props.nodeList[line.fr] as JourneyNode;

                delete line.runtime.frVertex;
                delete node_fr?.lnto[line.id];
                line.fr = null;
            }
            if (direction === 'to' && line.to) {
                let node_to = props.nodeList[line.to] as JourneyNode;

                delete line.runtime.toVertex;
                delete node_to?.lnfr[line.id];
                line.to = null;
            }
        };

        const movePoint = (ev: PointerEvent) => {
            // TODO: check if line is connected before detach
            if (!detachTriggered && Math.abs(o.x - ev.offsetX) + Math.abs(o.y - ev.offsetY) > 10) {
                detach();
                detachTriggered = true;
            }
            if (modSelect && Math.abs(o.x - ev.x) + Math.abs(o.y - ev.y) > 30) {
                editor.state.moving = true;
                modSelect = false;
            }

            cancelFrame(lineFrameReq);
            lineFrameReq = requestFrame(() => {
                let n_x = ~~((ev.x - o.x) * p_mod.value),
                    n_y = ~~((ev.y - o.y) * p_mod.value);

                if (isSelected && moveSelection) {
                    let { x, y } = snap ? calc_snap({ x: n_x, y: n_y }, snap_spacing) : { x: n_x, y: n_y };

                    if (x === editor.dev.props.sel_x && y === editor.dev.props.sel_y) return;

                    editor.dev.props.sel_x = x;
                    editor.dev.props.sel_y = y;

                    for (let nd of nodes) {
                        for (let to in nd.lnto) {
                            if (editor.tools.sel_lines[to]) continue;

                            props.data[to].runtime.toVertexMod.x = x;
                            props.data[to].runtime.toVertexMod.y = y;
                        }
                        for (let fr in nd.lnfr) {
                            if (editor.tools.sel_lines[fr]) continue;

                            props.data[fr].runtime.frVertexMod.x = x;
                            props.data[fr].runtime.frVertexMod.y = y;
                        }
                    }

                    for (let pt of points) {
                        if (editor.tools.sel_lines[pt.id]) continue;

                        if (typeof pt.idx === 'number') {
                            props.data[pt.id].points[pt.idx].x = pt.x + x;
                            props.data[pt.id].points[pt.idx].y = pt.y + y;
                        } else if (pt.idx === 'to') {
                            props.data[pt.id].ex = pt.ex + x;
                            props.data[pt.id].ey = pt.ey + y;
                        } else if (pt.idx === 'fr') {
                            props.data[pt.id].x = pt.x + x;
                            props.data[pt.id].y = pt.y + y;
                        }
                    }
                } else {
                    let { x, y } = snap ? calc_snap({ x: o.px + n_x, y: o.py + n_y }, snap_spacing) : { x: o.px + n_x, y: o.py + n_y };

                    if (x === target[px] && y === target[py]) return;

                    if (target) {
                        target[px] = x;
                        target[py] = y;
                    }
                }

                // target[px] = ev_x
                // target[py] = ev_y
            });
        };
        addDocumentListener('pointermove', movePoint);

        const pointerUpHandler = (ev: PointerEvent) => {
            var fr = props.nodeList[target.fr];
            var to = props.nodeList[target.to];

            // node_list;
            state.busy = false;
            editor.state.busy = false;
            editor.state.moving = false;

            cancelFrame(lineFrameReq);

            if (moveSelection) {
                for (let sn of nodes) {
                    sn.x += editor.dev.props.sel_x;
                    sn.y += editor.dev.props.sel_y;

                    for (let to in sn.lnto) {
                        if (editor.tools.sel_lines[to]) continue;

                        props.data[to].runtime.toVertexMod.x = 0;
                        props.data[to].runtime.toVertexMod.y = 0;
                    }
                    for (let fr in sn.lnfr) {
                        if (editor.tools.sel_lines[fr]) continue;

                        props.data[fr].runtime.frVertexMod.x = 0;
                        props.data[fr].runtime.frVertexMod.y = 0;
                    }
                }

                for (let ln of lines) {
                    for (let i = 0; i < ln.points.length; i++) {
                        let pt = ln.points[i];

                        props.data[ln.id].points[i].x = pt.x + editor.dev.props.sel_x;
                        props.data[ln.id].points[i].y = pt.y + editor.dev.props.sel_y;
                    }
                    let line = props.data[ln.id];

                    line.x += editor.dev.props.sel_x;
                    line.y += editor.dev.props.sel_y;
                    line.ex += editor.dev.props.sel_x;
                    line.ey += editor.dev.props.sel_y;
                }

                for (let pt of points) {
                    pt.x += editor.dev.props.sel_x;
                    pt.y += editor.dev.props.sel_y;
                }

                editor.dev.props.sel_x = 0;
                editor.dev.props.sel_y = 0;
            }

            if (
                (ev.shiftKey && typeof point.idx === 'number') ||
                (ev.shiftKey && ((point.idx === 'fr' && !props.data[point.id].to) || (point.idx === 'to' && !props.data[point.id].fr)))
            ) {
                editor.tools.sel_points[point.cid] ? delete editor.tools.sel_points[point.cid] : (editor.tools.sel_points[point.cid] = point);
            }

            const updateLineHitboxes = () => {
                let updated: any = {};

                for (let lid in editor.tools.sel_lines) {
                    if (!updated[lid]) {
                        updated[lid] = true;

                        let line = props.data[lid];

                        generateHitboxes(line);
                    }
                }

                for (let nid in editor.tools.sel_nodes) {
                    let node = props.nodeList[nid];

                    if (node.data.type === NodeType.Text) continue;
                    else node = node as JourneyNode;

                    for (let l in node.lnto)
                        if (!updated[l]) {
                            updated[l] = true;
                            generateHitboxes(props.data[l]);
                        }
                    for (let l in node.lnfr)
                        if (!updated[l]) {
                            updated[l] = true;
                            generateHitboxes(props.data[l]);
                        }
                }

                for (let pid in editor.tools.sel_points) {
                    let lid = editor.tools.sel_points[pid].id;

                    if (!updated[lid]) {
                        updated[lid] = true;
                        generateHitboxes(props.data[lid]);
                    }
                }
            };

            updateLineHitboxes();

            generateHitboxes(line);

            if (isSelected) {
                let n_x = ~~((ev.x - o.x) * p_mod.value),
                    n_y = ~~((ev.y - o.y) * p_mod.value);

                editor.tools.sel_dims.t = o.sel_area.y + n_y;
                editor.tools.sel_dims.b = o.sel_area.y + editor.tools.sel_dims.h + n_y;
                editor.tools.sel_dims.r = o.sel_area.x + editor.tools.sel_dims.w + n_x;
                editor.tools.sel_dims.l = o.sel_area.x + n_x;
            }

            direction &&
                connectToNode({
                    x: ev.x,
                    y: ev.y,
                    line,
                    direction,
                });

            editor.events.emit('calcselarea', {
                nodes: editor.tools.sel_nodes,
                points: editor.tools.sel_points,
                lines: editor.tools.sel_lines,
            });

            removeDocumentListener('pointermove', movePoint);
            removeDocumentListener('pointerup', pointerUpHandler);
        };
        addDocumentListener('pointerup', pointerUpHandler);
    };

    const connectToNode = ({ x, y, line, direction }: { x: number; y: number; line: JourneyLine; direction: string }) => {
        requestFrame(() => {
            let pointEl = document.elementFromPoint(x, y) as HTMLElement,
                nodeId = parseInt(pointEl.dataset.id!);

            if (!pointEl || !nodeId || pointEl.dataset.nodetype !== 'canvas-node' || [line.to, line.fr].indexOf(nodeId) !== -1) {
                // Connection failed - delete line if it's completely unconnected (both ends free)
                if (line.to === null && line.fr === null) {
                    delete props.data[line.id];
                }
                return;
            }

            let node = props.nodeList[nodeId];

            if (node.data.type === NodeType.Text) return;
            else node = node as JourneyNode;

            // updateConnectedConditionNodes(direction, line, node, props.nodeList)

            let nd = direction === 'to' ? node.lnfr : node.lnto;
            nd[line.id] = {
                id: line.id,
            };
            (line as Indexable<string, any>)[direction] = node.id;

            var frnode = props.nodeList[line.fr as number] as JourneyNode;
            var tonode = props.nodeList[line.to as number] as JourneyNode;

            if (!frnode || !tonode) {
                handleRemoveLine(line);
                delete nd[line.id];
                return;
            }

            var shouldConnect = validateConnectionType(frnode, tonode, Object.values(props.nodeList) as JourneyNode[], Object.values(props.data));

            if (!shouldConnect) {
                handleRemoveLine(line);
                delete nd[line.id];

                toast.emit('note', {
                    pos: 'top-left',
                    type: 'warning',
                    message: {
                        head: $Resources.JourneyInvalidConnectionHeader,
                        body: $Resources.JourneyInvalidConnectionBody.replace('{fromNode}', frnode.data.title || frnode.data.name).replace(
                            '{toNode}',
                            tonode.data.title || tonode.data.name
                        ),
                        break: true,
                    },
                });

                return;
            }
            node.runtime.relVertexFunc = calcRelVertex[RelVertexFunc.Circ];
            if ([NodeFrame.RoundedRect, NodeFrame.RoundedSquare].indexOf(node.f) >= 0) node.runtime.relVertexFunc = calcRelVertex[RelVertexFunc.Rect];
            else if ([NodeFrame.Rombus].indexOf(node.f) >= 0) node.runtime.relVertexFunc = calcRelVertex[RelVertexFunc.RotatedRect];

            // node.runtime.relVertexFunc = calcRelVertex[node.rvf as typeof RelVertexFunc.Circ]
            // node.runtime.relVertexFunc = calcRelVertex[RelVertexFunc.Circ]

            let points = line.points;

            if (direction === 'to') {
                line.to = node.id;

                line.runtime.toVertex = computed(() => {
                    node = node as JourneyNode;

                    let point = {
                        x: line.x,
                        y: line.y,
                        mdx: line.runtime.frVertexMod.x,
                        mdy: line.runtime.frVertexMod.y,
                    };

                    if (points.length > 0) {
                        let { x, y } = points[points.length - 1];
                        point.x = x;
                        point.y = y;
                    }

                    let { x, y } = node.runtime.relVertexFunc!(point, node, 1);

                    line.ex = x;
                    line.ey = y;
                    return `${x} ${y}`;
                });
            } else if (direction === 'fr') {
                line.fr = node.id;

                line.runtime.frVertex = computed(() => {
                    node = node as JourneyNode;

                    let point: LineVertex = {
                        x: line.ex,
                        y: line.ey,
                        mdx: line.runtime.toVertexMod.x,
                        mdy: line.runtime.toVertexMod.y,
                    };

                    if (points.length > 0) {
                        let { x, y } = points[0];
                        point.x = x;
                        point.y = y;
                    }

                    let { x, y } = node.runtime.relVertexFunc!(point, node, -1);

                    line.x = x;
                    line.y = y;
                    return `${x} ${y}`;
                });
            }
        });
    };

    const handleRemovePoint = (line: JourneyLine, idx: number, sel_id: string) => {
        line.points.splice(idx, 1);
        delete editor.tools.sel_points[sel_id];

        requestFrame(() => {
            editor.events.emit('calcselarea', {
                nodes: editor.tools.sel_nodes,
                points: editor.tools.sel_points,
                lines: editor.tools.sel_lines,
            });

            generateHitboxes(line);
        });
    };

    const handleRemoveLine = (line: JourneyLine) => {
        if (line.to) {
            const node_to = props.nodeList[line.to] as JourneyNode;
            if (node_to?.lnfr) delete node_to.lnfr[line.id];
        }
        if (line.fr) {
            const node_fr = props.nodeList[line.fr] as JourneyNode;
            if (node_fr?.lnto) delete node_fr.lnto[line.id];
        }

        let regexp = new RegExp('^l' + line.id + '\\D');
        for (let key in editor.tools.sel_points) {
            if (regexp.test(key)) delete editor.tools.sel_points[key];
        }

        delete props.data[line.id];

        editor.events.emit('calcselarea', {
            nodes: editor.tools.sel_nodes,
            points: editor.tools.sel_points,
            lines: editor.tools.sel_lines,
        });
    };

    editor.events.addListener('deleteline', handleRemoveLine);

    const changeVertexFunc = (node: JourneyNode) => {
        node.runtime.relVertexFunc = calcRelVertex[RelVertexFunc.Circ];
        if ([NodeFrame.RoundedRect, NodeFrame.RoundedSquare].indexOf(node.f) >= 0) node.runtime.relVertexFunc = calcRelVertex[RelVertexFunc.Rect];
        else if ([NodeFrame.Rombus].indexOf(node.f) >= 0) node.runtime.relVertexFunc = calcRelVertex[RelVertexFunc.RotatedRect];

        // node.runtime.relVertexFunc = calcRelVertex[node.rvf as typeof RelVertexFunc.Rect]
    };
    editor.events.addListener('changevertexfunc', changeVertexFunc);

    const line_colors = [
        { clr: 126, tooltip: '' },
        { clr: 6, tooltip: '' },
        { clr: 35, tooltip: '' },
        { clr: 159, tooltip: '' },
    ];
</script>

<template>
    <!-- <div> -->
    <svg
        class="line-canvas ZD_MIDDLE"
        :class="[state.busy && 'component-busy', editor.dev.show.hitboxes && 'dev_show-hitboxes']"
        xmlns="http://www.w3.org/2000/svg"
        v-when:drawline="drawLine">
        <defs>
            <marker
                v-for="{ clr } of line_colors"
                v-once
                :id="`arrow${clr}`"
                class="line-arrow-marker"
                viewBox="0 0 10 10"
                refX="5"
                refY="5"
                markerWidth="12"
                markerHeight="12"
                orient="auto-start-reverse"
                markerUnits="userSpaceOnUse">
                <path d="M 0 0 L 7 5 L 0 10 L 3 5 z" stroke-width="0" :fill="PresetColors[clr as 1].hsl" />
            </marker>

            <marker id="dot" class="line-point-marker" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5" markerHeight="5">
                <circle cx="5" cy="5" r="4" fill="red" />
            </marker>

            <g id="removebtn" class="line-btn-wrapper">
                <circle class="line-btn" cx="0" cy="0" r="6" fill="red" />
                <path d="M -3 -3 L 3 3 M 3 -3 L -3 3" stroke="#fff" stroke-width="2" fill="none" />
            </g>

            <g id="settingsbtn" class="line-btn-wrapper">
                <circle class="line-btn" cx="0" cy="0" r="7" fill="green" />
                <path
                    fill="white"
                    transform="translate(-4.5, -4.5) scale(.9)"
                    d="M9.4,5.5C9,5.1,8.9,4.4,9.4,4C10,3.4,9.8,3,9.3,2.4c-0.6-1-0.4-0.9-1.4-0.7C7.3,1.9,6.8,1.6,6.7,1.1C6.2-0.2,6.4,0,5.2,0 C4.1,0,3.9-0.1,3.6,1.1C3.5,1.7,2.7,2,2.2,1.9C1,1.7,1,1.7,0.5,2.7c-0.6,1.1-0.8,1,0.2,1.8c0.3,0.3,0.4,1,0.1,1.4 C0.1,6.6,0.1,6.6,0.7,7.4C1,7.9,1.2,8.5,2,8.3c1.1-0.3,1.4,0.7,1.7,1.5C4,10,4.5,10,4.9,9.9c1.2,0,1.2,0,1.7-1.2 c0.1-0.4,0.8-0.8,1.2-0.8C9,8.2,9,8.2,9.7,7.1C10.1,6.3,10.2,6.2,9.4,5.5z M6,6.5C3.9,7.7,2,4.6,4.1,3.4C6.1,2.2,8,5.3,6,6.5z" />
            </g>
        </defs>

        <transition-group name="medium-fade">
            <JourneyLineGroup
                v-for="line in data"
                :key="line.id"
                :line="line"
                :movePoint="handleMovePoint"
                :removePoint="handleRemovePoint"
                :removeLine="handleRemoveLine"
                :addPoint="addPoint"
                :colors="line_colors"
                :class="editor.tools.sel_lines[line.id] && 'invisible'" />
        </transition-group>
    </svg>
    <!-- </div> -->
</template>

<style>
    .invisible {
        opacity: 0;
    }

    .medium-fade-enter-active,
    .medium-fade-leave-active {
        transition: opacity 0.3s ease;
    }

    .medium-fade-enter-from,
    .medium-fade-leave-to {
        opacity: 0;
    }

    .medium-fade-enter-to,
    .medium-fade-leave-from {
        opacity: 1;
    }

    .line-canvas {
        --c: yellow;
        position: absolute;
        pointer-events: none;
    }
    .hitbox {
        stroke-width: 30;
        z-index: 20;
        pointer-events: all;
    }
    .dev_show-hitboxes .hitbox {
        stroke: rgba(0, 255, 221, 0.39);
    }
    .editor-busy .hitbox {
        pointer-events: none;
    }
    .line {
        position: absolute;
        z-index: 3;
    }

    .line-btns,
    .point-btns {
        cursor: pointer;
    }

    .point,
    .point-btns,
    .line-btns {
        opacity: 0;

        transition-property: opacity, transform;
        transition-duration: 0.3s, 0.14s;
        transition-delay: 0.6s, 0s;
        transition-timing-function: ease-out;

        pointer-events: all;
        will-change: transform;
    }

    .point.selected,
    .line-group:hover .point,
    .line-group:hover .line-btns:not(.concealed),
    .point-wrapper:hover .point-btns,
    .line-group:active :where(.point, .line-btns) {
        opacity: 1;
        transition-duration: 0.1s;
        transition-delay: 0.05s;
    }

    .editor-busy .hitbox,
    .editor-busy .line-btns {
        pointer-events: none;
    }

    .editor-busy :is(.line-btns, .point-btns) {
        opacity: 0 !important;
        transition: opacity 0.05s ease-in;
    }
</style>
